import { Moon, Sun } from 'lucide-react';
import type { Theme } from './ThemeProvider.js';
import { useTheme } from './useTheme.js';

export interface ThemeToggleProps {
  /** Optional override className for the <button>. */
  readonly className?: string;
}

/**
 * Two-state button: it flips the theme you SEE (the resolved theme), so one click always changes the screen.
 * The stored preference stays `system` until the first click; after that the explicit choice is stored.
 * Getting back to `system` is `setTheme('system')` (the user menu offers it). The icon shows the current theme;
 * the label says what the click does.
 */
export function ThemeToggle({ className = '' }: ThemeToggleProps) {
  const { resolved, setTheme } = useTheme();
  const next: Theme = resolved === 'dark' ? 'light' : 'dark';
  const label = next === 'dark' ? 'Switch to dark mode' : 'Switch to light mode';
  return (
    <button
      type="button"
      data-testid="theme-toggle"
      aria-label={label}
      title={label}
      onClick={() => setTheme(next)}
      className="inline-flex size-9 items-center justify-center rounded-xl border border-border bg-surface text-fg-muted transition-colors hover:bg-accent-soft hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {resolved === 'dark' ? <Moon className="size-4" /> : <Sun className="size-4" />}
    </button>
  );
}
